(() => {
  'use strict';

  const formatYen = (value) => {
    const amount = Number(value || 0);

    return `＋${new Intl.NumberFormat('ja-JP').format(amount)}円`;
  };

  const formatProductYen = (value) => {
    const amount = Number(value || 0);

    return `${new Intl.NumberFormat('ja-JP').format(amount)}円`;
  };

  class PikaProductOptions {
    constructor(root) {
      this.root = root;

      this.formId = root.dataset.productFormId || '';
      this.sectionId =
        root.dataset.sectionId ||
        this.detectSectionId();

      this.form = this.formId
        ? document.getElementById(this.formId)
        : null;

      this.totalElement = root.querySelector(
        '[data-pika-options-total]'
      );

      this.productTotalElement = root.querySelector(
        '[data-pika-product-total]'
      );

      this.summaryInput = root.querySelector(
        '[data-pika-option-summary-input]'
      );

      this.jsonInput = root.querySelector(
        '[data-pika-option-json-input]'
      );

      /*
       * ShopifyのLiquid価格は最小通貨単位。
       * 日本円でも 50,000円 → 5,000,000 と渡されるため、
       * JavaScript側では100で割って円へ変換する。
       */
      this.initialBasePriceCents = Number(
        root.dataset.basePrice || 0
      );

      this.currentBasePrice =
        this.initialBasePriceCents / 100;

      this.priceContainer = this.sectionId
        ? document.getElementById(
            `price-${this.sectionId}`
          )
        : null;

      this.variantContainer =
        document.querySelector(
          `variant-selects[data-section="${this.sectionId}"]`
        ) ||
        document.querySelector(
          `variant-radios[data-section="${this.sectionId}"]`
        );

      this.variantData = this.getVariantData();

      this.quantityInput =
        this.findQuantityInput();

      this.priceObserver = null;
      this.isUpdatingPrice = false;

      this.bindEvents();
      this.observePriceContainer();
      this.restoreAndUpdate();
    }

    detectSectionId() {
      const sectionElement =
        this.root.closest('[data-section]');

      if (sectionElement?.dataset.section) {
        return sectionElement.dataset.section;
      }

      const productSection =
        this.root.closest(
          '[id^="MainProduct-"], [id^="ProductInfo-"]'
        );

      if (!productSection) {
        return '';
      }

      return (
        productSection.dataset.section ||
        productSection.id
          .replace('MainProduct-', '')
          .replace('ProductInfo-', '')
      );
    }

    findQuantityInput() {
      if (this.form) {
        const formQuantity =
          this.form.querySelector(
            'input[name="quantity"]'
          );

        if (formQuantity) {
          return formQuantity;
        }
      }

      const section =
        this.root.closest(
          'section, [id^="MainProduct-"]'
        );

      return (
        section?.querySelector(
          'input[name="quantity"]'
        ) ||
        document.querySelector(
          'quantity-input input[name="quantity"]'
        )
      );
    }

    bindEvents() {
      this.root.addEventListener(
        'change',
        () => {
          this.update();
        }
      );

      this.form?.addEventListener(
        'submit',
        (event) => {
          this.validate(event);
        },
        true
      );

      if (this.variantContainer) {
        this.variantContainer.addEventListener(
          'change',
          () => {
            /*
             * global.js側でバリアント情報と価格HTMLを
             * 非同期更新するため、少し待ってから再計算する。
             */
            window.setTimeout(() => {
              this.updateCurrentBasePrice();
              this.update();
            }, 50);

            window.setTimeout(() => {
              this.updateCurrentBasePrice();
              this.update();
            }, 300);
          }
        );
      }

      if (this.quantityInput) {
        this.quantityInput.addEventListener(
          'change',
          () => {
            this.update();
          }
        );

        this.quantityInput.addEventListener(
          'input',
          () => {
            this.update();
          }
        );
      }

      document.addEventListener(
        'pika:refresh-product-options',
        () => {
          this.updateCurrentBasePrice();
          this.update();
        }
      );
    }

    getVariantData() {
      if (!this.variantContainer) {
        return [];
      }

      const jsonElement =
        this.variantContainer.querySelector(
          'script[type="application/json"]'
        );

      if (!jsonElement) {
        return [];
      }

      try {
        return JSON.parse(
          jsonElement.textContent
        );
      } catch (error) {
        console.error(
          'PIKA: バリアントJSONの解析に失敗しました。',
          error
        );

        return [];
      }
    }

    getSelectedVariantOptions() {
      if (!this.variantContainer) {
        return [];
      }

      if (
        this.variantContainer.tagName
          .toLowerCase() === 'variant-radios'
      ) {
        return [
          ...this.variantContainer.querySelectorAll(
            'fieldset'
          )
        ].map((fieldset) => {
          return (
            fieldset.querySelector(
              'input[type="radio"]:checked'
            )?.value || ''
          );
        });
      }

      return [
        ...this.variantContainer.querySelectorAll(
          'select'
        )
      ].map((select) => select.value);
    }

    getCurrentVariant() {
      if (!this.variantData.length) {
        return null;
      }

      const selectedOptions =
        this.getSelectedVariantOptions();

      if (!selectedOptions.length) {
        return null;
      }

      return (
        this.variantData.find((variant) => {
          return variant.options.every(
            (option, index) => {
              return (
                option ===
                selectedOptions[index]
              );
            }
          );
        }) || null
      );
    }

    updateCurrentBasePrice() {
      const currentVariant =
        this.getCurrentVariant();

      if (
        currentVariant &&
        currentVariant.price != null
      ) {
        /*
         * variant.priceも最小通貨単位なので100で割る。
         */
        this.currentBasePrice =
          Number(currentVariant.price) / 100;

        return;
      }

      /*
       * バリアントが取得できない場合は、
       * Liquidから渡された初期価格を使用。
       */
      this.currentBasePrice =
        this.initialBasePriceCents / 100;
    }

    getQuantity() {
      const quantity = Number(
        this.quantityInput?.value || 1
      );

      if (
        !Number.isFinite(quantity) ||
        quantity < 1
      ) {
        return 1;
      }

      return quantity;
    }

    getSelection() {
      const groups = [];
      let optionUnitTotal = 0;

      this.root
        .querySelectorAll(
          '[data-pika-option-group]'
        )
        .forEach((group) => {
          const selectedInputs = [
            ...group.querySelectorAll(
              '.pika-product-options__input:checked'
            )
          ];

          const values =
            selectedInputs.map((input) => {
              const price = Number(
                input.dataset.price || 0
              );

              optionUnitTotal += price;

              const variantId = Number(
                input.dataset.variantId || 0
                );

                return {
                id: input.value,
                label:
                    input.dataset.optionLabel ||
                    input.value,
                price,
                variantId:
                    Number.isInteger(variantId) &&
                    variantId > 0
                    ? variantId
                    : null
                };
            });

          groups.push({
            id: group.dataset.groupId || '',
            label:
              group.dataset.groupLabel || '',
            type:
              group.dataset.groupType || '',
            values
          });
        });

      const quantity = this.getQuantity();

      return {
        groups,
        quantity,
        optionUnitTotal,
        optionTotal:
          optionUnitTotal * quantity
      };
    }

    update() {
      this.updateCurrentBasePrice();

      const selection = this.getSelection();

      if (this.totalElement) {
        this.totalElement.textContent =
          formatYen(selection.optionTotal);
      }

      this.updateProductTotals(selection);
      this.updateHiddenInputs(selection);

      this.root.dispatchEvent(
        new CustomEvent(
          'pika:options:change',
          {
            bubbles: true,
            detail: {
              ...selection,
              baseUnitPrice:
                this.currentBasePrice,
              productTotal:
                (
                  this.currentBasePrice +
                  selection.optionUnitTotal
                ) * selection.quantity
            }
          }
        )
      );
    }

    updateProductTotals(selection) {
      const baseUnitPrice = Number(
        this.currentBasePrice || 0
      );

      const unitTotal =
        baseUnitPrice +
        selection.optionUnitTotal;

      const total =
        unitTotal * selection.quantity;

      /*
       * 追加オプション欄下部の商品合計。
       */
      if (this.productTotalElement) {
        this.productTotalElement.textContent =
          formatProductYen(total);
      }

      /*
       * 既存のprice.liquidが出力している上部価格。
       *
       * <num id="price-display">50,000円</num>
       */
      this.updateExistingPriceDisplay(total);
    }

    updateExistingPriceDisplay(total) {
      if (!this.sectionId) {
        return;
      }

      const priceWrapper =
        document.getElementById(
          `price-${this.sectionId}`
        );

      if (!priceWrapper) {
        return;
      }

      const priceDisplay =
        priceWrapper.querySelector(
          'num#price-display'
        );

      if (!priceDisplay) {
        return;
      }

      /*
       * MutationObserverが自分の変更を再検知して
       * 無限ループしないようにする。
       */
      this.isUpdatingPrice = true;

      priceDisplay.textContent =
        formatProductYen(total);

      requestAnimationFrame(() => {
        this.isUpdatingPrice = false;
      });
    }

    updateHiddenInputs(selection) {
      const summary = selection.groups
        .filter(
          (group) =>
            group.values.length > 0
        )
        .map((group) => {
          const valueLabels =
            group.values
              .map((value) => value.label)
              .join('、');

          return `${group.label}：${valueLabels}`;
        })
        .join(' / ');

      if (this.summaryInput) {
        this.summaryInput.value = summary;
      }

      if (this.jsonInput) {
        this.jsonInput.value =
          JSON.stringify({
            groups: selection.groups,
            quantity: selection.quantity,
            optionUnitTotal:
              selection.optionUnitTotal,
            optionTotal:
              selection.optionTotal
          });
      }

      this.root.dataset.selectedAddons =
        JSON.stringify(
            selection.groups
            .flatMap((group) => {
                return group.values.map((value) => {
                return {
                    groupId: group.id,
                    groupLabel: group.label,
                    id: value.id,
                    label: value.label,
                    price: value.price,
                    variantId: value.variantId
                };
                });
            })
            .filter((value) => value.variantId)
        );
    }

    observePriceContainer() {
      if (!this.priceContainer) {
        return;
      }

      this.priceObserver =
        new MutationObserver(() => {
          if (this.isUpdatingPrice) {
            return;
          }

          /*
           * global.jsがバリアント変更時に
           * price-{sectionId}のHTMLを丸ごと再描画する。
           * 再描画完了後にオプション込み価格を再反映する。
           */
          window.setTimeout(() => {
            this.updateCurrentBasePrice();
            this.update();
          }, 0);
        });

      this.priceObserver.observe(
        this.priceContainer,
        {
          childList: true,
          subtree: true,
          characterData: true
        }
      );
    }

    restoreAndUpdate() {
      /*
       * 通常ロード時。
       */
      this.update();

      /*
       * ブラウザバック時は、ブラウザがフォーム状態を
       * JavaScript実行後に復元することがあるため、
       * 複数タイミングで再計算する。
       */
      requestAnimationFrame(() => {
        this.update();
      });

      window.setTimeout(() => {
        this.update();
      }, 50);

      window.setTimeout(() => {
        this.update();
      }, 250);

      window.setTimeout(() => {
        this.update();
      }, 600);
    }

    validate(event) {
      let valid = true;

      this.root
        .querySelectorAll(
          '[data-pika-option-group][data-required="true"]'
        )
        .forEach((group) => {
          const selected =
            group.querySelector(
              '.pika-product-options__input:checked'
            );

          const errorElement =
            group.querySelector(
              '[data-pika-option-error]'
            );

          if (!selected) {
            valid = false;

            group.classList.add(
              'pika-product-options__group--error'
            );

            if (errorElement) {
              errorElement.textContent =
                `${
                  group.dataset.groupLabel ||
                  '追加オプション'
                }を選択してください。`;

              errorElement.hidden = false;
            }
          } else {
            group.classList.remove(
              'pika-product-options__group--error'
            );

            if (errorElement) {
              errorElement.hidden = true;
            }
          }
        });

      if (valid) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const firstError =
        this.root.querySelector(
          '.pika-product-options__group--error'
        );

      firstError?.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }

  const initialize = (
    scope = document
  ) => {
    scope
      .querySelectorAll(
        '[data-pika-product-options]'
      )
      .forEach((root) => {
        if (
          root.dataset.pikaInitialized ===
          'true'
        ) {
          return;
        }

        root.dataset.pikaInitialized =
          'true';

        new PikaProductOptions(root);
      });
  };

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initialize();
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initialize(event.target);
    }
  );

  window.addEventListener(
    'pageshow',
    () => {
      document
        .querySelectorAll(
          '[data-pika-product-options]'
        )
        .forEach((root) => {
          root.dispatchEvent(
            new Event('change', {
              bubbles: true
            })
          );
        });
    }
  );
})();